import { useEffect, useState } from 'react';
import { Archive, Loader2, Plus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { api } from '../../shared/api/client';
import { divisionApi, type DivisionOverview } from '../../shared/api/division.api';
import { useCachedQuery } from '../../shared/lib/queryCache';

interface PickableAccount {
  id: string;
  name: string;
  platform: string;
}

/**
 * Направления отдела: какие команды их снимают, кто модерирует и куда
 * выкладывают.
 *
 * Направление — это «что снимаем» (Алматы, Akorda News, ИИ-контент), команда —
 * «кто снимает». Одна команда ведёт несколько направлений с разными правилами:
 * у Алматы через модератора, у Akorda — без. Так их и различаем.
 *
 * Аккаунты направления — куда команда выкладывает ролики: монтажёры и
 * постановщик её команд видят эти аккаунты, а кнопка «Опубликовать» в
 * «Монтаже» ставит их первыми. Один аккаунт бывает в нескольких направлениях —
 * Instagram toqayev__vibe общий у Алматы и Астаны.
 */
export function ProjectsTab({
  divisionId, overview, onChanged, onPatch,
}: {
  divisionId: string;
  overview: DivisionOverview | undefined;
  onChanged: () => void;
  /** Показать правку сразу, не дожидаясь сервера. */
  onPatch?: (fn: (o: DivisionOverview) => DivisionOverview) => void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);

  const projects = overview?.projects ?? [];
  const groups = overview?.groups ?? [];
  const members = overview?.members ?? [];
  const moderators = members.filter((m) => m.role === 'moderator');
  const groupName = (id: string | null) => groups.find((g) => g.id === id)?.name ?? '—';
  const personName = (id: string) => {
    const m = members.find((x) => x.id === id);
    return m?.name ?? m?.username ?? id;
  };

  /** Любое изменение направления: одинаковая обвязка вокруг запроса. */
  async function run(key: string, action: () => Promise<unknown>): Promise<void> {
    setBusy(key);
    try {
      await action();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setBusy(null);
      // Перечитать в фоне: страница не пропадает (08.10.2026).
      onChanged();
    }
  }
  /** Переключатель направления: видно сразу, сервер — следом. */
  type Proj = DivisionOverview['projects'][number];
  function toggle(projectId: string, change: (p: Proj) => Proj, action: () => Promise<unknown>): void {
    onPatch?.((o) => ({ ...o, projects: o.projects.map((p) => (p.id === projectId ? change(p) : p)) }));
    void run(`${projectId}:t`, action);
  }
  const { data: accountData } = useCachedQuery<{ accounts: PickableAccount[] }>(
    `division:${divisionId}:accounts`,
    () => api.get<{ accounts: PickableAccount[] }>(`/division/${divisionId}/accounts`),
  );
  const accounts = accountData?.accounts ?? [];
  const accountLabel = (id: string) => {
    const a = accounts.find((x) => x.id === id);
    if (!a) return id;
    return a.platform === 'tiktok' ? `${a.name} · TikTok` : a.platform === 'youtube' ? `${a.name} · YouTube` : a.name;
  };

  async function toggleArchive(projectId: string, archived: boolean): Promise<void> {
    // Закрытое направление пропадает у всех с досок и из форм — без вопроса
    // его легко закрыть промахом (08.10.2026).
    const name = projects.find((p) => p.id === projectId)?.name ?? '';
    if (archived && !(await confirmDialog({ title: `Закрыть направление «${name}»?`, description: 'Оно пропадёт с досок и из новых публикаций. Вернуть можно здесь же.', confirmText: 'Закрыть', destructive: true }))) return;
    setBusy(projectId);
    try {
      await divisionApi.updateProject(projectId, { archived });
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <CreateDialog
          title="Новое направление"
          trigger="Завести направление"
          placeholder="Akorda News"
          onSubmit={(name) => divisionApi.createProject(divisionId, { name })}
          onDone={onChanged}
        />
      </div>

      {projects.length === 0 && (
        <p className="text-muted-foreground">
          Направлений пока нет. У направления свои аккаунты и состав команды.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {projects.map((project) => {
          const accountIds = project.accountIds ?? [];
          return (
          <section key={project.id} className="flex flex-col gap-3 rounded-xl border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{project.name}</h3>
                {project.archived && <Badge variant="secondary">закрыто</Badge>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy === project.id}
                onClick={() => toggleArchive(project.id, !project.archived)}
              >
                {busy === project.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Archive className="h-4 w-4" />{project.archived ? 'Вернуть' : 'Закрыть'}</>}
              </Button>
            </div>

            {project.name === 'Президент' && (
              <div className="flex flex-col gap-2 rounded-lg bg-muted/40 p-3 text-sm">
                <span className="font-medium">Отделения</span>
                {project.groupIds.filter((id) => /^(Алматы|Алмата|Астана)$/i.test(groupName(id))).map((id) => (
                  <div key={id} className="flex flex-wrap items-center gap-2">
                    <span className="min-w-20 font-medium">{groupName(id)}</span>
                    {members.filter((member) => member.groupId === id && project.memberIds?.some((entry) => entry.userId === member.id)).map((member) => (
                      <Badge key={member.id} variant="secondary">
                        {personName(member.id)} · {member.role === 'editor' ? 'Монтажёр' : 'Контент-мейкер'}
                      </Badge>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Люди прямо в направлении (08.10.2026): команды больше не нужны. */}
            {(['content_maker', 'editor'] as const).map((role) => {
              const inProject = (project.memberIds ?? []).filter((m) => m.role === role);
              const candidates = members.filter((m) => m.role === role && !inProject.some((x) => x.userId === m.id));
              const key = `${project.id}:${role}`;
              return (
                <div key={role} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{role === 'content_maker' ? 'Контент-мейкер:' : 'Монтажёры:'}</span>
                  {inProject.length === 0 && <span className="text-muted-foreground">никого</span>}
                  {inProject.map(({ userId }) => (
                    <Badge key={userId} variant="secondary" className="gap-1">
                      {personName(userId)}
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Убрать ${personName(userId)} из направления`}
                        onClick={() => run(key, () => divisionApi.removeProjectMember(project.id, userId))}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                  <Select
                    value=""
                    onValueChange={(userId) => run(key, () => divisionApi.addProjectMember(project.id, userId))}
                    disabled={busy === key || candidates.length === 0}
                  >
                    <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder={role === 'content_maker' ? '+ контент-мейкер' : '+ монтажёр'} /></SelectTrigger>
                    <SelectContent>
                      {candidates.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name ?? m.username}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Модерирует:</span>
              {project.moderatorIds.length === 0 && (
                <span className="text-muted-foreground">
                  никто — ролик сразу принимает контент-мейкер
                </span>
              )}
              {project.moderatorIds.map((userId) => (
                <Badge key={userId} variant="secondary" className="gap-1">
                  {personName(userId)}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Снять ${personName(userId)} с модерации`}
                    onClick={() => run(`${project.id}:m`, () => divisionApi.removeProjectModerator(project.id, userId))}
                  >
                    ×
                  </button>
                </Badge>
              ))}
              <Select
                value=""
                onValueChange={(userId) => run(`${project.id}:m`, () => divisionApi.addProjectModerator(project.id, userId))}
                disabled={busy === `${project.id}:m` || moderators.every((m) => project.moderatorIds.includes(m.id))}
              >
                <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="+ модератор" /></SelectTrigger>
                <SelectContent>
                  {moderators.filter((m) => !project.moderatorIds.includes(m.id)).map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name ?? m.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {moderators.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  в отделе нет модераторов — роль даётся на вкладке «Состав»
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Аккаунты:</span>
              {accountIds.length === 0 && (
                <span className="text-muted-foreground">не указаны — опубликовать из «Монтажа» будет некуда</span>
              )}
              {accountIds.map((accountId) => (
                <Badge key={accountId} variant="secondary" className="gap-1">
                  {/* Модерация — по аккаунту (08.10.2026): щит включает проверку
                      модератором направления для роликов в этот аккаунт. */}
                  {project.moderatorIds.length > 0 && (
                    <button
                      type="button"
                      title={project.moderatedAccountIds?.includes(accountId) ? 'Модерация включена — нажмите, чтобы выключить' : 'Без модерации — нажмите, чтобы включить'}
                      className={project.moderatedAccountIds?.includes(accountId) ? 'text-review-fg' : 'text-muted-foreground/40 hover:text-muted-foreground'}
                      onClick={() => {
                        const on = !project.moderatedAccountIds?.includes(accountId);
                        toggle(project.id, (p) => ({
                          ...p,
                          moderatedAccountIds: on
                            ? [...(p.moderatedAccountIds ?? []), accountId]
                            : (p.moderatedAccountIds ?? []).filter((x) => x !== accountId),
                        }), () => divisionApi.moderateProjectAccount(project.id, accountId, on));
                      }}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {accountLabel(accountId)}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Убрать ${accountLabel(accountId)} из направления`}
                    onClick={() => run(`${project.id}:a`, () => divisionApi.removeProjectAccount(project.id, accountId))}
                  >
                    ×
                  </button>
                </Badge>
              ))}
              <Select
                value=""
                onValueChange={(accountId) => run(`${project.id}:a`, () => divisionApi.addProjectAccount(project.id, accountId))}
                disabled={busy === `${project.id}:a`}
              >
                <SelectTrigger className="h-7 w-52 text-xs"><SelectValue placeholder="+ аккаунт" /></SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => !accountIds.includes(a.id)).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{accountLabel(a.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="flex items-center gap-2">
                <Switch
                  checked={!project.selfPublish}
                  onCheckedChange={(on) => toggle(project.id, (p) => ({ ...p, selfPublish: !on }),
                    () => divisionApi.updateProject(project.id, { selfPublish: !on }))}
                  aria-label="Приёмка контент-мейкером"
                />
                <span className="text-muted-foreground">Приёмка:</span>
                {project.selfPublish ? 'нет — монтажёры публикуют сами' : 'принимает контент-мейкер'}
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="flex items-center gap-2">
                <Switch
                  checked={project.backup !== false}
                  onCheckedChange={(on) => toggle(project.id, (p) => ({ ...p, backup: on }),
                    () => divisionApi.updateProject(project.id, { backup: on }))}
                  aria-label="Бэкап в Telegram"
                />
                <span className="text-muted-foreground">Бэкап в Telegram:</span>
                {project.backup !== false ? 'ролики уходят в канал' : 'нет — только скачать с сайта'}
              </label>
              {project.backup !== false && (
                <ChatField
                  value={project.chatId ?? null}
                  disabled={busy === `${project.id}:c`}
                  onSave={(chatId) => run(`${project.id}:c`, () => divisionApi.updateProject(project.id, { chatId }))}
                />
              )}
            </div>
          </section>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Канал направления (08.10.2026): куда бот шлёт принятые ролики. Команд на
 * экранах больше нет, канал живёт здесь. Пусто — канал команды монтажёра, как
 * было до переноса. Бота надо добавить в канал админом.
 */
function ChatField({ value, disabled, onSave }: { value: number | null; disabled: boolean; onSave: (v: number | null) => void }) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => setText(value == null ? '' : String(value)), [value]);
  const clean = text.trim();
  const valid = clean === '' || /^-?\d{5,20}$/.test(clean);
  const changed = clean !== (value == null ? '' : String(value));
  const save = () => { if (valid && changed) onSave(clean === '' ? null : Number(clean)); };
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-muted-foreground">канал</span>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        disabled={disabled}
        placeholder="ID, например -1001234567890"
        title="ID Telegram-канала. Бот должен быть в нём администратором."
        className={`h-7 w-56 rounded-md border bg-background px-2 text-xs ${valid ? 'border-input' : 'border-destructive'}`}
      />
      {!value && <span className="text-xs text-muted-foreground">не задан — в канал команды монтажёра</span>}
    </span>
  );
}

/** Одно поле и кнопка: и проект, и паблик заводятся одинаково — именем. */
function CreateDialog({
  title, trigger, placeholder, variant, initial, submitLabel, onSubmit, onDone,
}: {
  title: string;
  trigger: string;
  placeholder: string;
  variant?: 'outline' | 'ghost';
  initial?: string;
  submitLabel?: string;
  onSubmit: (name: string) => Promise<unknown>;
  onDone: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(initial ?? '');
  const [sending, setSending] = useState(false);

  async function submit(): Promise<void> {
    if (!name.trim()) return;
    setSending(true);
    try {
      await onSubmit(name.trim());
      setOpen(false);
      setName(initial ?? '');
      onDone();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={variant}>
          {!initial && <Plus className="h-4 w-4" />}
          {trigger}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="create-name">Название</Label>
          <Input
            id="create-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
            placeholder={placeholder}
          />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!name.trim() || sending}>
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel ?? 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
